import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import _ from 'lodash';
import styled, { css } from 'styled-components';
import moment, { weekdaysMin, momentPropTypes } from '@g4/moment-utc';
import animations from '@g4/animations';
import hooks, { SELECT_DIRECTION, DAY_LIST } from '@g4/automation-hooks';

import Text from '@g4/boneyard/lib/Text';
import oldTheme from '../Boneyard/Colors';

import Day, {
  DAY_TYPE_EMPTY,
  DAY_TYPE_AVAILABLE,
  DAY_TYPE_ACTIVE,
  DAY_SHAPE_DEFAULT,
  DAY_SHAPE_SINGLE,
  DAY_SHAPE_LEADING,
  DAY_SHAPE_TRAILING,
  DAY_POSITION_START,
  DAY_POSITION_DEFAULT,
  DAY_POSITION_END,
} from './Day';

import checkedIcon from '../../images/ic-checked.svg';

export const MODE_START = 'DATE/SELECTION/MODE/START';
export const MODE_END = 'DATE/SELECTION/MODE/END';

export const TYPE_SINGLE = 'DATE/SINGLE';
export const TYPE_RANGE = 'DATE/RANGE';

const { scrollTo } = animations;

export const Content = styled.div`
  flex: 1; /* Fill available space in order to push .daysSelected to the bottom of the container */
  overflow-y: auto; /* Ensures .daysSelected is pinned to the bottom */
`;

export const Calendar = styled.div`
  border: 1px solid ${props => oldTheme.colors.modern.datePicker.border};
  max-width: 500px;
  margin: 0 auto;
  text-align: center;
  padding-top: ${props => props.theme.spacing(2)};
  background-color: #fff;
`;

const calendarList = css`
  list-style: none;
  display: flex;
  width: 100%;
  padding-left: 0;
  margin: 0;
`;

export const DayNameList = styled.ul`
  ${calendarList};
  justify-content: space-around;
  color: ${props => oldTheme.colors.modern.datePicker.dayList};
  padding: ${props => props.theme.spacing(2)};
`;

export const DayName = styled.li`
  width: calc(100% / 7);
`;

export const DayList = styled.ul`
  ${calendarList};
  padding: 0 ${props => props.theme.spacing(2)};
  flex-flow: row wrap;
  display: table;
  text-align: start;
  overflow: hidden;
`;

export const Tabs = styled.nav`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
`;

export const InteractiveTab = styled.button`
  flex: 1 0 50%;
  text-align: left;
  padding: 0.625rem 1.25rem;
  /* Reduce right padding to allow content to wrap at a later point */
  padding-right: 0.3215rem;
  border: 1px solid ${props => oldTheme.colors.modern.datePicker.border};
  font-size: 0.75rem;
  line-height: 1.5;
  background-color: ${props => oldTheme.colors.modern.datePicker.activeTab};
  ${props =>
    props.selected &&
    css`
      background-color: ${oldTheme.colors.modern.datePicker.selected};
    `} color: ${props => oldTheme.colors.modern.datePicker.tab};
  font-weight: bold;
  display: flex;
  flex-direction: column;

  ${props =>
    props.active &&
    !props.isOneWay &&
    css`
      background-color: ${oldTheme.colors.modern.datePicker.activeTab};
    `};
`;

export const LegLabel = styled.span`
  margin-right: 0.625rem;
`;

export const Image = styled.img`
  max-height: 13px;
`;

export const TabDate = styled.span`
  /* Ensures the entire date wraps to a newline on small viewports */
  display: inline-block;
`;

export const MonthName = styled.span`
  flex: 1;
  font-weight: 400;
`;

export const SelectionMessage = styled.div`
  border: 1px solid ${props => oldTheme.colors.modern.datePicker.border};
  border-bottom: 0;
  padding: 1rem 0;
  text-align: center;
  background-color: ${props => oldTheme.colors.common.notification};
`;

export const MonthSwitcher = styled(InteractiveTab.withComponent('nav'))`
  display: flex;
  flex-direction: row;
  text-align: center;
  color: ${props => oldTheme.colors.modern.datePicker.active};
  background-color: #fff;
  border: 0;
  justify-content: space-between;
  align-items: center;
  min-height: 60px;
  padding: 0 1.125rem;
`;

export const PreviousMonthControl = styled.button.attrs({ type: 'button' })`
  float: left;
`;

export const NextMonthControl = styled.button.attrs({ type: 'button' })`
  float: right;
`;

export const LegTextWrapper = styled.span`
  display: flex;
`;

/** @visibleName Datepicker */
export class DateRangePicker extends PureComponent {
  static propTypes = {
    /** `Moment.js` object representing the start of the selected date range */
    start: momentPropTypes.utc,
    /** `Moment.js` object representing the end of the selected date range */
    end: momentPropTypes.utc,
    /** Determines whether a single date, or a date range, is to be picked */
    type: PropTypes.oneOf([TYPE_SINGLE, TYPE_RANGE]),
    /** Determines if the start or the end of the date range is to be picked */
    mode: PropTypes.oneOf([MODE_START, MODE_END]),
    /** Function to execute when `mode` prop is changed */
    onModeChange: PropTypes.func,
    /** Function to execute when `scope` prop is changed */
    onScopeChange: PropTypes.func,
    /** Object containing information regarding restrictions as to which dates can be picked */
    restrictions: PropTypes.object,
    /** Determines whether the start/end date tabs are displayed */
    renderTabs: PropTypes.bool,
    /** Determines whether the month switcher to be displayed */
    renderMonthSwitcher: PropTypes.bool,
    /** Determines whether the selection message to be displayed */
    renderSelectionMessage: PropTypes.bool,
    /** injects the generated stylesheet */
    className: PropTypes.string,
    /* the calendar scope to be displayed */
    scope: momentPropTypes.utc,
    /* month switcher component to be rendered */
    monthSwitcher: PropTypes.node,
    /*  Determines whether the selected start/end date should be highlighted */
    highlightBothSelections: PropTypes.bool,
    // Text replacements
    departingFlightAriaLabel: PropTypes.string,
    returningFlightAriaLabel: PropTypes.string,
    dateRangePickerDepartingButton: PropTypes.string.isRequired,
    dateRangePickerReturningButton: PropTypes.string.isRequired,
    previousMonthAriaLabel: PropTypes.string.isRequired,
    leftArrowAltAttribute: PropTypes.string.isRequired,
    nextMonthAriaLabel: PropTypes.string.isRequired,
    rightArrowAltAttribute: PropTypes.string.isRequired,
    dateRangePickerNoAvailableDates: PropTypes.string.isRequired,
    dateRangePickerSelectedSameDay: PropTypes.string.isRequired,
    dateRangePickerSelectDate: PropTypes.string.isRequired,
    checkIconAltAttribute: PropTypes.string.isRequired,
    dateRangePickerSelectedDay: PropTypes.func.isRequired,
    // Standard functions
    onChange: PropTypes.func,
    /** The EXCLUSIVE = false datepicker is used for e.g. flights, where selecting only one day will write in the # of days strip 1 days selected */
    /** The EXCLUSIVE = true datepicker is used for e.g. hotels, where selecting two days will write in the # of days strip 1 night selected */
    exclusive: PropTypes.bool,
  };

  static defaultProps = {
    onChange: Function.prototype,
    onDismiss: Function.prototype,
    onScopeChange: Function.prototype,
    type: TYPE_RANGE,
    mode: MODE_START,
    onModeChange: Function.prototype,
    restrictions: {},
    renderTabs: true,
    renderMonthSwitcher: true,
    renderSelectionMessage: true,
    highlightBothSelections: false,
    exclusive: false,
  };
  constructor(props) {
    super(props);

    const { mode, start, end } = this.props;
    this.state = {
      mode,
      start,
      end,
    };

    this.toRef = this.toRef.bind(this);
    this.handleSetMode = this.handleSetMode.bind(this);
    this.handleAddScopeMonths = this.handleAddScopeMonths.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleStartTabClick = _.partial(this.handleSetMode, MODE_START);
    this.handleEndTabClick = _.partial(this.handleSetMode, MODE_END);
    this.handlePreviousMonthClick = _.partial(this.handleAddScopeMonths, -1);
    this.handleNextMonthClick = _.partial(this.handleAddScopeMonths, 1);
  }

  handleAddScopeMonths(amount) {
    this.props.onScopeChange(this.props.scope.clone().add(amount, 'months'));
  }

  handleSetMode(mode) {
    let scope = this.props.scope;
    if (mode === MODE_START && this.state.start) {
      scope = this.state.start.clone();
    } else if (mode === MODE_END && this.state.end) {
      scope = this.state.end.clone();
    }

    this.setState({ mode });
    this.props.onScopeChange(scope);
    this.props.onModeChange(mode);
  }

  handleChange(date) {
    var { mode, start, end } = this.state;

    if (this.isSingle() || this.isStartActive()) {
      start = date.clone();
      if (start && end && end.isBefore(start, 'day')) {
        end = null;
      }
      if (this.isRange()) {
        mode = MODE_END;
      }
    } else if (this.isEndActive() && this.isRange()) {
      this.scrollDown();
      end = date.clone();
    }

    this.setState({ start, end });
    this.props.onChange({ start, end });
    if (mode !== this.props.mode) {
      this.handleSetMode(mode);
    }
  }

  isRange() {
    return this.props.type === TYPE_RANGE;
  }

  isSingle() {
    return this.props.type === TYPE_SINGLE;
  }

  isStartActive() {
    return (
      (this.isRange() && this.state.mode === MODE_START) || this.isSingle()
    );
  }

  isEndActive() {
    return this.isRange() && this.state.mode === MODE_END;
  }

  isDateAvailable(date) {
    const restrictions = this.getCurrentRestrictions();
    const { start } = this.state;

    if (this.isRange() && this.isEndActive()) {
      if (start && date.isBefore(start)) {
        return false;
      }
    }
    if (restrictions.length) {
      return restrictions.indexOf(date.format('YYYY-MM-DD')) !== -1;
    } else {
      return false;
    }
  }

  getCurrentRestrictions() {
    const { restrictions } = this.props;

    if (this.isSingle() || this.isStartActive()) {
      return restrictions.start || [];
    } else if (this.isRange() && this.isEndActive()) {
      return restrictions.end || [];
    }
  }

  buildCalendar() {
    const { scope, highlightBothSelections } = this.props;
    const { start, end } = this.state;

    const isRange = this.isRange();
    const isSingle = this.isSingle();

    const isEndActive = this.isEndActive();
    const isStartActive = this.isStartActive();

    const scopeStart = scope.clone().startOf('month');
    const scopeEnd = scope.clone().endOf('month');
    const monthRange = scopeStart.twix(scopeEnd, { allDay: true });
    const paddedRange = scopeStart
      .clone()
      .startOf('week')
      .twix(scopeEnd.clone().endOf('week'));
    const iter = paddedRange.iterate('days');
    const calendar = [];
    while (iter.hasNext()) {
      let date = iter.next();
      let inCurrentScope = monthRange.contains(date);

      let today = moment().isSame(date, 'day');

      let type = DAY_TYPE_AVAILABLE;
      let shape = DAY_SHAPE_DEFAULT;
      let position = DAY_POSITION_DEFAULT;
      let disabled = false;
      let highlighted = false;

      let firstEmpty = false;

      const last = calendar[calendar.length - 1];

      if (inCurrentScope) {
        if (
          isRange &&
          start &&
          end &&
          date.isBetween(start, end, 'day', '[]')
        ) {
          type = DAY_TYPE_ACTIVE;
        }

        if (start && date.isSame(start, 'day')) {
          type = DAY_TYPE_ACTIVE;
          shape = DAY_SHAPE_LEADING;
          if (isSingle || !end) {
            shape = DAY_SHAPE_SINGLE;
          }
          if (isStartActive || highlightBothSelections) {
            highlighted = true;
          }
        }

        if (isRange && end && date.isSame(end, 'day')) {
          type = DAY_TYPE_ACTIVE;
          shape = DAY_SHAPE_TRAILING;
          if (start.isSame(end)) {
            shape = DAY_SHAPE_SINGLE;
          }
          if (isEndActive || highlightBothSelections) {
            highlighted = true;
          }
        }

        let dateCopy = date.clone();

        if (date.isSame(dateCopy.startOf('week'), 'day')) {
          position = DAY_POSITION_START;
        } else if (date.isSame(dateCopy.endOf('week'), 'day')) {
          position = DAY_POSITION_END;
        }

        if (!this.isDateAvailable(date)) {
          disabled = true;
        }
      } else {
        type = DAY_TYPE_EMPTY;

        if (isRange && start && end && last) {
          if (parseInt(last.date.format('D'), 10) === last.date.daysInMonth()) {
            if (date.isBetween(start, end, 'day', '[]')) {
              firstEmpty = true;
            }
          }
        }
      }

      if (
        isRange &&
        start &&
        end &&
        last &&
        [DAY_SHAPE_TRAILING, DAY_SHAPE_DEFAULT].includes(shape) &&
        last.type === DAY_TYPE_EMPTY &&
        type === DAY_TYPE_ACTIVE &&
        date.isBetween(start, end, 'day', '[]')
      ) {
        last.lastEmpty = true;
      }

      calendar.push({
        date,
        highlighted,
        today,
        type,
        shape,
        position,
        disabled,
        firstEmpty,
      });
    }

    return calendar;
  }

  renderTabs() {
    const startActive = this.isStartActive();
    const endActive = this.isEndActive();
    const { start, end } = this.state;

    if (!this.props.renderTabs) {
      return null;
    }

    // The departing day is selected but the returning isn't. Also the departing tab isn't active.
    // When the departing tab is active and no returning date was selected, we want to render only the departing section
    const returningNotSelected = this.isRange() && start && !startActive;
    // The departing and returning days are selected. Also the departing tab is active.
    // In this case we want to be able to navigate through tabs.
    const returningSelected = this.isRange() && start && startActive && end;

    return (
      <Tabs>
        <InteractiveTab
          active={startActive}
          aria-label={this.props.departingFlightAriaLabel}
          type="button"
          onClick={this.handleStartTabClick}
          selected={!!start}
          isOneWay={this.isSingle()}
          {...hooks.hook(SELECT_DIRECTION, 'departing')}
        >
          <LegTextWrapper>
            <LegLabel>
              <Text size={12}>{this.props.dateRangePickerDepartingButton}</Text>
            </LegLabel>
            {start && (
              <Image src={checkedIcon} alt={this.props.checkIconAltAttribute} />
            )}
          </LegTextWrapper>
          {start ? (
            <TabDate>
              <Text size={16}>{start.format('ddd, MMM D, YYYY')}</Text>
            </TabDate>
          ) : (
            <TabDate>
              <Text size={16}>{this.props.dateRangePickerSelectDate}</Text>
            </TabDate>
          )}
        </InteractiveTab>
        {(returningNotSelected || returningSelected) && (
          <InteractiveTab
            active={endActive}
            aria-label={this.props.returningFlightAriaLabel}
            type="button"
            disabled={!start}
            onClick={this.handleEndTabClick}
            selected={!!end}
            {...hooks.hook(SELECT_DIRECTION, 'returning')}
          >
            <LegTextWrapper>
              <LegLabel>
                <Text size={12}>
                  {this.props.dateRangePickerReturningButton}
                </Text>
              </LegLabel>
              {end && (
                <Image
                  src={checkedIcon}
                  alt={this.props.checkIconAltAttribute}
                />
              )}
            </LegTextWrapper>
            {end ? (
              <TabDate>
                <Text size={16}>{end.format('ddd, MMM D, YYYY')}</Text>
              </TabDate>
            ) : (
              <TabDate>
                <Text size={16}>{this.props.dateRangePickerSelectDate}</Text>
              </TabDate>
            )}
          </InteractiveTab>
        )}
      </Tabs>
    );
  }

  daySelectionMessage() {
    const { start, end } = this.state;
    const endDateSelected = !!end && this.isRange();
    const restrictions = this.getCurrentRestrictions();

    if (!this.props.renderSelectionMessage) {
      return null;
    }

    if (!restrictions.length) {
      return (
        <SelectionMessage>
          <Text size={12}>{this.props.dateRangePickerNoAvailableDates}</Text>
        </SelectionMessage>
      );
    }

    if (!endDateSelected) {
      return null;
    }

    // If the type of the datepicker is EXCLUSIVE = false then we need to take into account the full dates selected,
    // e.g. selecting a date from 1st-5th of a month will show as 5 days selected in the SelectionMessage strip, while the
    // EXCLUSIVE type will show 4, at the time of this comment EXCLUSIVE =false  is used for flights and EXC = true for hotels rental
    const totalDays =
      this.props.exclusive === false
        ? end.diff(start, 'days') + 1
        : end.diff(start, 'days');

    return (
      <SelectionMessage>
        {!totalDays && (
          <Text size={12}>{this.props.dateRangePickerSelectedSameDay}</Text>
        )}
        {!!totalDays && (
          <Text size={12}>
            {this.props.dateRangePickerSelectedDay(totalDays)}
          </Text>
        )}
      </SelectionMessage>
    );
  }

  toRef(el) {
    this.contentEl = el;
  }

  scrollDown() {
    const el = this.contentEl;
    scrollTo(el.scrollHeight, el);
  }

  render() {
    return (
      <div className={this.props.className}>
        <Content ref={this.toRef}>
          {this.renderTabs()}
          {this.props.monthSwitcher}
          <Calendar>
            <DayNameList aria-hidden="true">
              {weekdaysMin().map((v, i) => (
                <DayName key={i}>
                  <Text size={14}>{v.charAt(0)}</Text>
                </DayName>
              ))}
            </DayNameList>
            <DayList {...hooks.hook(DAY_LIST)}>
              {this.buildCalendar().map((day, i) => (
                <Day key={i} {...day} onClick={this.handleChange} />
              ))}
            </DayList>
          </Calendar>
        </Content>
        {this.daySelectionMessage()}
      </div>
    );
  }
}

const StyledDateRangePicker = styled(DateRangePicker)`
  display: flex;
  flex-direction: column;
  flex: 1; /* Since we could be directly within a Dismissible */
  background-color: ${props => oldTheme.colors.modern.GreyA100};
`;

export default StyledDateRangePicker;
